import { Network, Organisation } from '@common/types/objects';
import { EComponentTypes } from '@common/definitions';

type ComponentTypesKeys = keyof typeof EComponentTypes;
export type ComponentTypesValues = typeof EComponentTypes[ComponentTypesKeys];

/**
 * Feature visibility from `enabled_components` only (aligned with mobile).
 *
 * `available_components` is plan-only and belongs in community settings, not here.
 *
 * - Community (network selected): on if it is in org or network `enabled_components`.
 * - Admin (no network): on if it is in org `enabled_components`.
 */
export function hasEnabledComponent(
  requiredComponent: ComponentTypesValues,
  network: Network | null | undefined,
  organisation: Organisation | null | undefined,
  isOnlyNetwork: boolean = false
) {
  if (isOnlyNetwork) {
    return network?.enabled_components?.includes(requiredComponent) || false;
  }

  const isEnabledOnOrganisation =
    organisation?.enabled_components?.includes(requiredComponent) || false;

  if (!network) return isEnabledOnOrganisation;

  const isEnabledOnNetwork =
    network.enabled_components?.includes(requiredComponent) || false;

  return isEnabledOnOrganisation || isEnabledOnNetwork;
}

export function hasEnabledComponentOnAnyNetwork(
  requiredComponent: ComponentTypesValues,
  networks: Network[] | null | undefined,
  organisation: Organisation | null | undefined,
) {
  if (hasEnabledComponent(requiredComponent, undefined, organisation)) return true;

  return (networks || []).some((network) => (
    hasEnabledComponent(requiredComponent, network, undefined, true)
  ));
}

export function getUserNetworkIds(user?: {
  networks?: Array<string | { id: string }> | null;
  scopes?: { networks?: Array<{ id: string }> | null };
} | null): string[] {
  const scoped = user?.scopes?.networks;
  if (Array.isArray(scoped) && scoped.length > 0) {
    return scoped.map((network) => network.id).filter(Boolean);
  }

  if (!Array.isArray(user?.networks)) return [];

  return user.networks
    .map((network) => (typeof network === 'string' ? network : network?.id))
    .filter(Boolean);
}

export function hasEnabledComponentForUserNetworks(
  requiredComponent: ComponentTypesValues,
  organisation: Organisation | null | undefined,
  organisationNetworks: Network[] | null | undefined,
  userNetworkIds: string[] | null | undefined,
) {
  const ids = userNetworkIds || [];

  // Missing community ids (admin celebration payload, some popovers): org or any community.
  if (ids.length === 0) {
    return hasEnabledComponentOnAnyNetwork(
      requiredComponent,
      organisationNetworks,
      organisation,
    );
  }

  const idSet = new Set(ids);
  const userNetworks = (organisationNetworks || []).filter((network) => idSet.has(network.id));

  return hasEnabledComponentOnAnyNetwork(requiredComponent, userNetworks, organisation);
}

